// src/cms/boards/BoardMasters.jsx
import React, { useEffect, useMemo, useState } from "react";
import NewWindow from "../components/NewWindow";
import { apiAxios } from "../api/mockApi";
import { readSession, saveSession, boardMasters } from "../auth/RequireAuth";

/* =========================
   샘플 + 로컬스토리지 CRUD
========================= */
const genBbsId = () =>
  "BBSMSTR_" + Math.random().toString(36).slice(2, 12).toUpperCase();

const ROLE_KEYS = [
  "ROLE_ADMIN",
  "ROLE_ANONYMOUS",
  "ROLE_USER_COMPANY",
  "ROLE_USER_MEMBER",
];

// Y/N ↔ boolean
const yToBool = (v) => v === "Y" || v === true;
const boolToY = (b) => (b ? "Y" : "N");

// ROLE_AUTH 파싱/직렬화
function parseRoleAuth(str = "") {
  const base = ROLE_KEYS.reduce((acc, k) => {
    acc[k] = { R: false, W: false };
    return acc;
  }, {});
  str
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((token) => {
      const suffix = token.endsWith("W") ? "W" : token.endsWith("R") ? "R" : "";
      const name = suffix ? token.slice(0, -1) : token;
      if (base[name] && (suffix === "R" || suffix === "W")) {
        base[name][suffix] = true;
      }
    });
  return base;
}
function stringifyRoleAuth(map = {}) {
  const out = [];
  ROLE_KEYS.forEach((name) => {
    const row = map[name] || { R: false, W: false };
    if (row.R) out.push(name + "R");
    if (row.W) out.push(name + "W");
  });
  return out.join(",");
}

// 날짜 포맷
const toYmdhm = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  const p = (n) => (n < 10 ? "0" + n : n);
  return `${dt.getFullYear()}-${p(dt.getMonth() + 1)}-${p(dt.getDate())} ${p(
    dt.getHours()
  )}:${p(dt.getMinutes())}`;
};


/* =========================
    상세창
========================= */
function DetailWindow({ item, onClose, onSaved, onDeleted }) {
  const [form, setForm] = useState(() => {
    // 편집용 상태: reply는 boolean, role은 매트릭스로 변환
    const initial = item || {
      ID: null,
      BBS_ID: "",
      BBS_TY : "",
      BBS_TY_CODE: "",
      BBS_NM: "",
      USE_AT: "N",
      REPLY_POSBL_A: "N",
      FILE_ATCH_POSBL_AT: 0,
      ROLE_AUTH: "",
      LAST_UPDT: toYmdhm(new Date()),
    };
    return {
      ...initial,
      _reply: yToBool(initial.REPLY_POSBL_A),
      _role: parseRoleAuth(initial.ROLE_AUTH),
    };
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    const safeBbsId = (form.BBS_ID && String(form.BBS_ID).trim()) || genBbsId();
    const payload = {
      ID: form.ID,
      BBS_ID: safeBbsId,
      BBS_TY_CODE: form.BBS_TY_CODE,
      BBS_NM: form.BBS_NM,
      USE_AT: boolToY(form._useAt),
      REPLY_POSBL_A: boolToY(form._reply),
      FILE_ATCH_POSBL_AT: Number(form.FILE_ATCH_POSBL_AT) || 0,
      ROLE_AUTH: stringifyRoleAuth(form._role),
      LAST_UPDT: toYmdhm(new Date()),
    };
    
  };

  const remove = () => {
    if (!form.ID) return onDeleted && onDeleted();
    onDeleted && onDeleted();
  };

  const boardList = readSession(boardMasters);

  return (
    <div style={{ minWidth: 900 }}>
      <div className="win-toolbar">
        <span className="title">
          {form.ID ? "게시판생성 수정" : "게시판생성 등록"}
        </span>
        <div className="d-flex gap-2">
          {form.ID && (
            <button className="btn btn-sm btn-outline-danger" onClick={remove}>
              <i className="fa-regular fa-trash-can me-1" />
              삭제
            </button>
          )}
          <button className="btn btn-sm btn-secondary" onClick={onClose}>
            닫기
          </button>
          <button className="btn btn-sm btn-primary" onClick={save}>
            <i className="fa-regular fa-floppy-disk me-1" />
            저장
          </button>
        </div>
      </div>

      <div className="win-body">
        {/* 1행: 게시판명 | 게시판 유형 */}
        <div className="row g-2 input-rounded mb-2">
          <form className="col-lg-6">
            <label className="form-label mb-1">게시판명 <span style={{color:"#dc3545"}}>*</span></label>
            <input
              className="form-control"
              value={form.BBS_NM}
              onChange={(e) => set("BBS_NM", e.target.value)}
            />
          </form>
          <form className="col-lg-6">
            <label className="form-label mb-1">게시판 유형 <span style={{color:"#dc3545"}}>*</span></label>
            <select
              className="form-select"
              value={form.BBS_TY_CODE}
              onChange={(e) => set("BBS_TY_CODE", e.target.value)}
            >
              {boardList.map((row) => (
                <option value={row.BBS_TY_CODE}>{row.BBS_TY}</option>
              ))}
              </select>
          </form>
        </div>

        {/* 2행: 코드 | 등록/수정일 */}
        <div className="row g-2 input-rounded mb-2">
          {/* 숨김 필드로 유지(필요 시) */}
          <input type="hidden" value={form.BBS_ID || ""} readOnly />
          <form className="col-lg-4 ms-auto">
            <label className="form-label mb-1">등록/수정일 <span style={{color:"#dc3545"}}>*</span></label>
            <input className="form-control" value={form.LAST_UPDT} disabled />
          </form>
        </div>

        {/* 3행: 답장 가능 / 첨부 가능 숫자 */}
        <div className="row g-2 input-rounded mb-3">
          <div className="col-12 d-flex align-items-center gap-4 flex-wrap">
            <div className="form-label mb-0">답장가능여부 : <span style={{color:"#dc3545"}}>*</span></div>
            <div className="form-check">
              <input
                id="reply-y"
                className="form-check-input"
                type="radio"
                name="reply"
                checked={!!form._reply}
                onChange={() => set("_reply", true)}
              />
              <label htmlFor="reply-y" className="form-check-label ms-1">
                답장 가능
              </label>
            </div>
            <div className="form-check">
              <input
                id="reply-n"
                className="form-check-input"
                type="radio"
                name="reply"
                checked={!form._reply}
                onChange={() => set("_reply", false)}
              />
              <label htmlFor="reply-n" className="form-check-label ms-1">
                답장 불가능
              </label>
            </div>

            <div className="vr mx-2" />

            <label className="form-label mb-0">첨부파일 가능 숫자 <span style={{color:"#dc3545"}}>*</span></label>
            <select
              className="form-select"
              style={{ width: 120 }}
              value={form.FILE_ATCH_POSBL_AT}
              onChange={(e) =>
                set("FILE_ATCH_POSBL_AT", Number(e.target.value))
              }
            >
              {[0, 1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={n}>
                  {n}개
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* 권한 매트릭스 */}
        <div className="mb-2">
          <div className="form-label mb-2">사용권한 <span style={{color:"#dc3545"}}>*</span></div>
          <div className="table-responsive">
            <table className="table table-bordered align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 40 }} className="text-center">
                    #
                  </th>
                  <th>역할</th>
                  <th style={{ width: 120 }} className="text-center">
                    읽기(R)
                  </th>
                  <th style={{ width: 120 }} className="text-center">
                    쓰기(W)
                  </th>
                </tr>
              </thead>
              <tbody>
                {ROLE_KEYS.map((role, i) => (
                  <tr key={role}>
                    <td className="text-center">{i + 1}</td>
                    <td>{role}</td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={form._role?.[role]?.R || false}
                        onChange={(e) =>
                          set("_role", {
                            ...form._role,
                            [role]: {
                              ...(form._role?.[role] || { R: false, W: false }),
                              R: e.target.checked,
                            },
                          })
                        }
                      />
                    </td>
                    <td className="text-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={form._role?.[role]?.W || false}
                        onChange={(e) =>
                          set("_role", {
                            ...form._role,
                            [role]: {
                              ...(form._role?.[role] || { R: false, W: false }),
                              W: e.target.checked,
                            },
                          })
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* 저장 시 stringifyRoleAuth(form._role) 로 직렬화됨 */}
        </div>
      </div>
    </div>
  );
}

/* =========================
   메인: 필터 + 리스트
========================= */
export default function BoardMasters() {
  const [detail, setDetail] = useState(null);
  const [list, setList] = useState(null);

  const [draft, setDraft] = useState({ type: "ALL", keyword: "" });
  const [filters, setFilters] = useState({ type: "ALL", keyword: "" });

  const onSearch = () => setFilters({ ...draft });
  const openNew = () => setDetail({}); // 등록

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const boardList = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const url = "/scms/api/bbs/selectBoardList.do";
      const body = JSON.stringify({
                uniqId : JSON.parse(window.sessionStorage.getItem("userInfo")).uniqId,
              });
      apiAxios(url, body, function(res) {
        saveSession(boardMasters, res.list);
        setList(res.list);
      });
    } catch(e) {
      setError(e);
    }
    setLoading(false);
  };
      
  useEffect(() => {
    boardList();
  }, []);

  const filtered = useMemo(() => {
    if(list != null) {
      return list.filter((m) => {
        if (filters.type !== "ALL" && m.BBS_TY_CODE !== filters.type) return false;
        if (filters.keyword) {
          const k = filters.keyword.toLowerCase();
          const nm = (m.BBS_NM || "").toLowerCase();
          const code = (m.BBS_ID || "").toLowerCase();
          if (!nm.includes(k) && !code.includes(k)) return false;
        }
        return true;
      });
    }
  }, [list, filters]);

  if(loading) return <div> Loading... </div>
  if(error) return <div> 에러가 발생했습니다. </div>

  if(!list) return null;
  if(!filtered) return <div> Filter is undefined. </div> 

  return (
    <div className="cms-card">
      {/* 🔎 검색 필터 */}
      <div className="cms-card mb-3 filterBox">
        <div className="row g-2 align-items-end input-rounded">
          {/* <div className="col-md-3 col-6">
            <label className="form-label mb-1">게시판 유형</label>
            <select
              className="form-select form-select-sm"
              value={draft.type}
              onChange={(e) => setDraft({ ...draft, type: e.target.value })}
            >
              <option value="ALL">전체</option>
              <option value="공지게시판">공지게시판</option>
              <option value="문의게시판">문의게시판</option>
            </select>
          </div> */}
          <div className="col-md-7 col-6">
            <label className="form-label mb-1">검색</label>
            <input
              className="form-control form-control-sm"
              placeholder="게시판명으로 검색"
              value={draft.keyword}
              onChange={(e) => setDraft({ ...draft, keyword: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && onSearch()}
            />
          </div>
          <div className="col-md-2 col-12 text-end">
            <div className="d-flex gap-2 justify-content-end">
              <button className="btn btn-sm btn-primary" onClick={onSearch}>
                조회
              </button>
              <button className="btn btn-sm btn-outline-primary" onClick={openNew}>
                <i className="fa-solid fa-plus me-1" />
                등록
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 📋 리스트 */}
      <table className="table table-hover table-rounded">
        <thead>
          <tr>
            <th style={{ width: 80, textAlign: "center" }}>번호</th>
            <th>게시판명</th>
            <th style={{ width: 180 }}>게시판유형</th>
            <th style={{ width: 100, textAlign: "center" }}>사용여부</th>
            <th style={{ width: 180, textAlign: "center" }}>등록일시</th>
            <th style={{ width: 80 }}></th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((m) => (
            <tr key={m.ID} style={{ cursor: "pointer" }} onClick={() => setDetail(m)}>
              <td style={{ textAlign: "center" }}>{m.ID}</td>
              <td>{m.BBS_NM}</td>
              <td>{m.BBS_TY}</td>
              <td style={{ textAlign: "center" }}>{m.USE_AT}</td>
              <td style={{ textAlign: "center" }} className="text-muted small">{m.LAST_UPDT || "-"}</td>
              <td className="text-end" onClick={(e) => e.stopPropagation()}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setDetail(m)}
                >
                  <i className="fa-regular fa-eye" />
                </button>
              </td>
            </tr>
          ))}
          {!filtered.length && (
            <tr>
              <td colSpan={5} className="text-center text-muted py-5">
                데이터가 없습니다.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* 상세 새 창 */}
      {detail && (
        <NewWindow
          title={detail?.ID ? "게시판생성 수정" : "게시판생성 등록"}
          onClose={() => {
            setDetail(null);
            //refresh();
          }}
        >
          <DetailWindow
            item={detail?.ID ? detail : null}
            onClose={() => {
              setDetail(null);
              //refresh();
            }}
            onSaved={() => {
              setDetail(null);
              //refresh();
            }}
            onDeleted={() => {
              setDetail(null);
              //refresh();
            }}
          />
        </NewWindow>
      )}
    </div>
  );
}
